import { useId, type SVGAttributes } from "react";

function Star({
  fillPercentage,
  ...props
}: { fillPercentage: number } & SVGAttributes<SVGSVGElement>) {
  const id = useId();
  const clipWidth =
    fillPercentage === 0.5 ? "12" : fillPercentage === 1 ? "24" : "0";

  return (
    <svg width="24" height="24" viewBox="0 0 24 24" {...props}>
      <defs>
        <clipPath id={id}>
          <rect x="0" y="0" width={clipWidth} height="24" />
        </clipPath>
      </defs>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.5118 4.25696C11.5747 4.09995 11.7274 3.99787 11.8961 4.00009H12.1042C12.2714 3.99653 12.4225 4.09967 12.4805 4.25696L14.4738 8.98506L19.5892 9.39445C19.7538 9.40453 19.8946 9.51681 19.9414 9.67541L20.0055 9.87609C20.0581 10.0276 20.0138 10.1959 19.8934 10.3015L16.0028 13.6329L17.1556 18.6259C17.1946 18.7855 17.1347 18.953 17.0035 19.0513L16.8274 19.1717C16.759 19.2255 16.6741 19.2539 16.5872 19.252C16.5139 19.2536 16.4417 19.2341 16.3791 19.1958L12.0002 16.5227L7.64528 19.1958C7.58267 19.2341 7.51045 19.2536 7.43714 19.252C7.35025 19.2539 7.26539 19.2255 7.19699 19.1717L6.99685 19.0513C6.86561 18.953 6.80572 18.7855 6.84475 18.6259L7.99751 13.6329L4.11495 10.3096C3.98833 10.2046 3.9404 10.0315 3.99487 9.87609L4.08293 9.67541C4.12975 9.51681 4.27052 9.40453 4.43516 9.39445L9.55054 8.98506L11.5118 4.25696ZM17.3397 10.6226L13.4891 10.3176L12.0002 6.73741L10.5112 10.3176L6.66063 10.6226L9.59857 13.1352L8.69397 16.892L12.0002 14.8771L15.2983 16.892L14.4018 13.1352L17.3397 10.6226Z"
        fill="currentColor"
      />
      <path
        d="M11.8961 3.75009C11.7274 3.74787 11.5747 3.84995 11.5118 4.00696L9.55054 8.73506L4.43516 9.14445C4.27052 9.15453 4.12975 9.26681 4.08293 9.42541L3.99487 9.62609C3.9404 9.78154 3.98833 9.95456 4.11495 10.0596L7.99751 13.3829L6.84475 18.3759C6.80572 18.5355 6.86561 18.703 6.99685 18.8013L7.19699 18.9217C7.26539 18.9755 7.35025 19.0039 7.43714 19.002C7.51045 19.0036 7.58267 18.9841 7.64528 18.9458L12.0002 16.2727L16.3791 18.9458C16.4417 18.9841 16.5139 19.0036 16.5872 19.002C16.6741 19.0039 16.759 18.9755 16.8274 18.9217L17.0035 18.8013C17.1347 18.703 17.1946 18.5355 17.1556 18.3759L16.0028 13.3829L19.8934 10.0515C20.0138 9.94591 20.0581 9.77756 20.0055 9.62609L19.9414 9.42541C19.8946 9.26681 19.7538 9.15453 19.5892 9.14445L14.4738 8.73506L12.4805 4.00696C12.4225 3.84967 12.2714 3.74653 12.1042 3.75009H11.8961Z"
        fill="currentColor"
        clipPath={`url(#${id})`}
      />
    </svg>
  );
}

export function Rating({
  value,
  size = "default",
}: {
  size?: "default" | "small";
  value: number;
}) {
  if (value < 0.0 || value > 5.0) {
    throw new Error("Value must be between 0.0 and 5.0");
  }

  const fullStars = Math.floor(value);
  const halfStar = value % 1 >= 0.5 ? 1 : 0;
  const emptyStars = 5 - fullStars - halfStar;

  return (
    <div className="flex items-center gap-px *:shrink-0">
      {Array(fullStars)
        .fill(0)
        .map((star: number, i) => (
          <Star
            className={size === "small" ? "size-[12px]" : ""}
            // eslint-disable-next-line @eslint-react/no-array-index-key
            key={star + i}
            fillPercentage={1}
          />
        ))}
      {halfStar > 0 ? (
        <Star
          className={size === "small" ? "size-[12px]" : ""}
          fillPercentage={0.5}
        />
      ) : null}
      {Array(emptyStars)
        .fill(0)
        .map((star: number) => (
          <Star
            className={size === "small" ? "size-[12px]" : ""}
            key={star + fullStars + halfStar}
            fillPercentage={0}
          />
        ))}
    </div>
  );
}
